/* Built In Imports */
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box, Heading } from '@chakra-ui/react';
import filter from 'lodash/filter';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import ProgressiveImage from '@components/UI/ProgressiveImage';
import HeadComponent from '@components/Utility/HeadComponent';
import { setAudioPlayer } from '@components/Utility/utils';
import config from '@config';
import MidContainer from '@Layout/MidContainer';
import PageConfig from '@msr/Common/PageConfig';
import RelatedContent from '@msr/components/Card/RelatedContent';
import ShivaTopic from '@msr/components/Card/ShivaTopic';
import MediaEmbed from '@msr/components/Embeds/MediaEmbed';
import WrapLayout from '@msr/components/Layout/WrapLayout';
import Breadcrum from '@msr/components/Navigation/Breadcrum';
import MsrImageSlider from '@msr/components/Tables/MsrImagerSlider';
import SubscribeForm from '@msr/components/UI/Blogs/SubscribeForm';
import StructuredTextContent from '@msr/components/UI/StructuredTextContent';
import SocialIconMob from '@msr/components/Utility/SocialIconMob';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
  fetchFooterDetailData,
  fetchHeaderMenuData
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/msrService';

/**
 * @param pageData.pageData
 * @param {object} pageData
 * @param pageData.lang
 * @returns
 */
const ShivaBlogLang = ({ pageData, lang }) => {
  const router = useRouter();
  const authContext = useContext(AuthContext);
  const [isAudioLoad, setAudioLoad] = useState(false);
  const imagegallery = pageData?.imageGallery;

  const embedObj = {
    embedUrl: pageData?.videoUrl,
    source: 'youtube',
  };

  useEffect(() => {
    authContext.RegionAndLang('', lang);
    if (typeof window !== 'undefined' && pageData?.types == 'audio' && isAudioLoad) {
      setAudioPlayer(pageData?.audioUrl);
    }
  }, [authContext, '', lang, isAudioLoad]);

  let topicsUnAvailable = filter(pageData?.topic, function (topic) {
    return topic?.title === '';
  });

  const handleAudioClick = () => {
    if (!isAudioLoad) {
      setAudioLoad(true);
    }
  };

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
      />
      <Breadcrum
        page={pageData?.breadcrumbs}
        alsoin={pageData?.alsoin}
        url=""
        region={''}
        lang={'en'}
      />
      <Box
        as="section"
        className="container-article"
        p="0"
        pt={{ base: '0', md: '64px' }}
        bg="#FFF7E9"
        dir={lang == "fa" ? "rtl" : ""}
      >
        <MidContainer w={'758'}>
          <Box>
            {imagegallery?.length > 0 ? (
              <MsrImageSlider sectionContent={imagegallery} />
            ) : pageData?.types === 'video' ? (
              <Box
                w={{ base: '100%' }}
                mb={{ base: '40px' }}
                pt={{ base: '30px', md: '0px' }}
              >
                <MediaEmbed sectionContent={embedObj} margin="0" />
              </Box>
            ) : (
              <ProgressiveImage
                src={pageData?.cardImage?.url}
                title={pageData?.cardImage?.title}
                alt={pageData?.cardImage?.alt}
                width="100%"
                height={{ base: '215px', sm: 'auto', lg: '454px' }}
                objectFit="cover"
              />
            )}
          </Box>
          {pageData?.types == 'audio' && pageData?.audioUrl && (
            <Box className="audio-player">
              <Box className="timeline">
                <Box className="progress"></Box>
              </Box>
              <Box className="controls">
                <Box className="play-container">
                  <Box
                    className="toggle-play play"
                    onClick={handleAudioClick}
                  ></Box>
                </Box>
                <Box className="time">
                  <Box className="current"></Box>
                  <Box className="length"></Box>
                </Box>
                <Box className="volume-container">
                  <Box className="volume-button">
                    <Box className="volume icono-volumeMedium"></Box>
                  </Box>
                  <Box className="volume-slider">
                    <Box className="volume-percentage"></Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </MidContainer>
        <MidContainer w={{ base: '90%', lg: '755px' }}>
          <Box
            fontSize={{ base: '36px', lg: '40px' }}
            lineHeight="48px"
            textAlign="center"
            fontFamily="FedraSerifAStdBook"
            mt={{ base: '40px', lg: '60px' }}
            mb="20px"
            as="h1"
            fontWeight="600"
          >
            {pageData?.title}
          </Box>
          <Box
            fontFamily="FedraSansStd-book"
            fontSize="16px"
            textAlign="center"
            mb="20px"
          >
            {pageData?.summary}
          </Box>
          <Box display="flex" placeContent="center" mb="43px">
            <LazyLoadImageComponent
              src={`${config.imgPath}/d/46272/1668597237-blue-lotus-vector.png`}
            />
          </Box>
        </MidContainer>
        <MidContainer w={'758'}>
          <MidContainer w={{ base: '90%', lg: '755px' }}>
            <Box
              fontFamily="FedraSansStd-book"
              className="structured-content-dark shivablogbody"
              fontSize="16px"
              mb="43px"
            >
            {StructuredTextContent(pageData?.body, "", lang)}
            </Box>
            <SocialIconMob
              data={pageData}
              url={router.asPath}
              isVisible={true}
              direction={'row'}
              position={'static'}
              top="30%"
              left="10%"
              display={{ base: 'flex', md: 'none', xl: 'none' }}
              likesCount={pageData?.likestotalcount}
            />
          </MidContainer>

          {topicsUnAvailable.length < pageData?.topic?.length && (
            <MidContainer w={'768'}>
              <Heading
                as="h4"
                fontSize="26px"
                lineHeight="25px"
                mt="60px"
                ml={{ base: '16px', md: 3, lg: 0 }}
                fontFamily="FedraSansStd-book"
                fontWeight="bold"
              >
                Related Tags
              </Heading>
              <ShivaTopic
                topics={pageData?.topic}
                bgTheme="light"
                lang={lang}
                page="shiva/topic"
              />
            </MidContainer>
          )}
        </MidContainer>
        <SubscribeForm />
        {pageData?.related_content && (
          <RelatedContent posts={pageData?.related_content} lang={lang} />
        )}
      </Box>
      <PageConfig
        isLikeVisible={true}
        likesCount={pageData?.likestotalcount}
        shareTextColor="#000000"
        hideInMobile={true}
        pageData={pageData}
      />

      <style global jsx>
        {`
          .shivablogbody p {
            color: #000000;
          }
          .shivablogbody button a {
            color: #ffffff;
          }
          .shivablogbody a {
            color: #cf4520;
            border-bottom: 1px solid #cf4520;
            padding-bottom: 2px;
            cursor: pointer;
            word-break: break-word;
          }
          .shivablogbody a:hover {
            background: #cf4520;
            border-bottom: none;
            color: #ffffff !important;
          }
          .shivablogbody ol,
          .shivablogbody ul {
            padding-left: 1.5em;
          }
          .shivablogbody ul li,
          .shivablogbody ol li {
            padding: 3px 0.6vw;
          }
        `}
      </style>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const msrPaths = config.USE_LOCAL_URL
    ? [
      `/mahashivratri/hi/shiva/why-is-shiva-the-destroyer`,
      `/mahashivratri/ta/shiva/forms-of-shiva`,
      `/mahashivratri/or/shiva/shiva-panchakshari-mantra-in-odia/`,
      `/mahashivratri/ta/shiva/63-nayanmargal-names-in-tamil/`,
      `/mahashivratri/kn/shiva/5-facts-about-mahashivratri`,
      `/mahashivratri/es/shiva/shiva-ganesha-parvati`,
      `/mahashivratri/ru/shiva/shiva-untold-wedding-shiva-parvathi`,
      `/mahashivratri/kn/shiva/shiva-tandava-stotram-kannada-lyrics-meaning`,
      `/mahashivratri/de/shiva/names-of-shiva-with-meaning`,
      `/mahashivratri/ta/shiva/shiva-tandava-stotram-lyrics-in-tamil`
    ]
    : await getLandingPaths('L2', '/shiva/', 'shiva_section', [
      '/shiva/power-of-the-mantra',
      '/topic/',
    ]);
  return {
    paths: msrPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { lang, blog },
}) => {
  const headerMenuData = await fetchHeaderMenuData(
    getLocale('', lang, 'msr'),
    'msrmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale('', lang, 'msr'),
    'msrfooter'
  );
  const pageData = await fetchDetails('', lang, `shiva/${blog}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      lang: lang,
      menuType: 'msrmainmenu',
    },
  };
};

export default ShivaBlogLang;
